import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import Login from "./auth/Login";
import AppShell from "./shell/AppShell";
import Dashboard from "./views/Dashboard";
import CasesList from "./views/CasesList";
import NotificationsHistory from "./views/NotificationsHistory";
import NewCase from "./views/NewCase";
import AuditQueue from "./views/AuditQueue";
import Personas from "./views/Personas";
import LateEntry from "./views/LateEntry";
import MyQueue from "./views/insumos/MyQueue";
import RepartoInsumosDispatcher from "./views/reparto_insumos/Dispatcher";
import TeamQueue from "./views/insumos/TeamQueue";
import InsumosQueueView from "./views/insumos/InsumosQueueView";
import Fallos from "./views/fallos/Fallos";
import FallosHistoric from "./views/fallos/FallosHistoric";
import FallosFiltered from "./views/fallos/FallosFiltered";
import Despacho from "./views/Despacho";
import AuditoriaDiaria from "./views/admin/AuditoriaDiaria";
import ActuacionesUrgentes from "./views/ActuacionesUrgentes";
import Carpetas from "./views/carpetas/CarpetasTracking";
import Contestaciones from "./views/Contestaciones";
import Maintenance from "./views/admin/Maintenance";
import UserInsumosCases from "./views/admin/UserInsumosCases";
import MisCasos from "./views/abogado/MisCasos";
import AdminUsers from "./views/admin/Users";
import AdminAuditLog from "./views/admin/AuditLogViewer";
import AdminSlaBreaches from "./views/admin/SlaBreaches";
import AdminErrorQueue from "./views/admin/ErrorQueue";
import AdminLateEntry from "./views/admin/LateEntryQueue";
import AdminExports from "./views/admin/Exports";
import AdminDevueltos from "./views/admin/Devueltos";
import AdminMonthlyReport from "./views/admin/MonthlyReport";
// feature/bot-litisoft — sprint90. Admin-only LITISOFT bot cockpit.
// feature/eva-litisoft — the five Bot-LITISOFT pages are now tabs inside one
// admin hub. The hub imports those view components internally.
import EvaLitisoft from "./views/admin/eva-litisoft";
import DirectoraDashboard from "./views/directora/DirectoraDashboard";
import ModerationQueue from "./views/moderation/Queue";
import ParaArchivar from "./views/reparto/ParaArchivar";
import Invite from "./views/Invite";
import { ToastProvider } from "./components/Toast";
import { CaseLauncherProvider } from "./components/CaseLauncher";

function RequireAuth({ children, roles }) {
  const { user, bootstrapping } = useAuth();
  const loc = useLocation();
  if (bootstrapping) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  // feature/multi-role-arch — accept the route if ANY of the user's granted
  // roles satisfies the gate, not only the primary `user.role`. Without this,
  // a multi-role user (e.g. abogado + insumos) couldn't visit the secondary
  // role's pages.
  if (roles) {
    const userRoles = (user.roles && user.roles.length) ? user.roles
                    : (user.role ? [user.role] : []);
    const allowed = userRoles.some((r) => roles.includes(r));
    if (!allowed) return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function HomeRedirect() {
  const { user, bootstrapping } = useAuth();
  if (bootstrapping) return null;
  if (!user) return <Navigate to="/login" replace />;
  const dest = ({
    admin: "/dashboard",
    reparto: "/dashboard",
    insumos: "/insumos/mine",
    revisor_fallos: "/fallos",
    abogado: "/abogado",
    directora: "/directora",
    carpetas: "/carpetas",
    // feature/litisoft-role — sprint87. Cristian lands on his only workspace.
    litisoft: "/admin/exports",
  })[user.role] || "/dashboard";
  return <Navigate to={dest} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/invite/:token" element={<Invite />} />
            <Route element={<RequireAuth><CaseLauncherProvider><AppShell /></CaseLauncherProvider></RequireAuth>}>
              <Route path="/" element={<HomeRedirect />} />

              {/* feature/role-views — admin + reparto */}
              <Route path="/dashboard"  element={<RequireAuth roles={["admin","reparto"]}><Dashboard /></RequireAuth>} />
              <Route path="/cases"      element={<RequireAuth roles={["admin","reparto"]}><CasesList /></RequireAuth>} />
              <Route path="/cases/new"  element={<RequireAuth roles={["admin","reparto"]}><NewCase /></RequireAuth>} />
              <Route path="/cases/late" element={<RequireAuth roles={["admin","reparto"]}><LateEntry /></RequireAuth>} />
              <Route path="/audit"      element={<RequireAuth roles={["admin","reparto"]}><AuditQueue /></RequireAuth>} />
              <Route path="/personas"   element={<RequireAuth roles={["admin","reparto"]}><Personas /></RequireAuth>} />

              {/* feature/role-views–4h placeholders */}
              <Route path="/insumos"        element={<RequireAuth roles={["admin","insumos"]}><TeamQueue /></RequireAuth>} />
              <Route path="/insumos/team"   element={<RequireAuth roles={["admin","insumos"]}><TeamQueue /></RequireAuth>} />
              <Route path="/insumos/mine"   element={<RequireAuth roles={["admin","insumos"]}><MyQueue /></RequireAuth>} />
              {/* feature/reparto-followup — INSUMOS sub-routes (4 filtered queues).
                  Sprint 57c — /insumos/solicitados (sidebar label "Solicitados",
                  the URL César actually clicks) now renders the same Sprint 57b
                  two-tab Pipeline UI as /insumos/mine. The old "Solicitados"
                  flat list shipped fallos in by accident and predates the
                  two-tab split César asked for. Recibidos / Adjudicados /
                  Devueltos sub-modes keep their filter-specific InsumosQueueView
                  rendering. */}
              <Route path="/insumos/cola"           element={<Navigate to="/insumos/mine" replace />} />
              <Route path="/insumos/solicitados"    element={<RequireAuth roles={["admin","insumos","directora"]}><MyQueue /></RequireAuth>} />
              <Route path="/insumos/recibidos"      element={<RequireAuth roles={["admin","insumos","directora"]}><InsumosQueueView mode="recibidos" /></RequireAuth>} />
              <Route path="/insumos/adjudicados"    element={<RequireAuth roles={["admin","insumos","directora"]}><InsumosQueueView mode="adjudicados" /></RequireAuth>} />
              <Route path="/insumos/devueltos"      element={<RequireAuth roles={["admin","insumos","directora"]}><InsumosQueueView mode="devueltos" /></RequireAuth>} />
              {/* feature/reparto-insumos-dispatcher. Camilo's 3-section dispatcher. */}
              <Route path="/reparto-insumos"        element={<RequireAuth roles={["admin","directora","reparto_insumos"]}><RepartoInsumosDispatcher /></RequireAuth>} />
              {/* feature/moderation-escalation. */}
              <Route path="/moderacion"             element={<RequireAuth roles={["admin","directora","insumos"]}><ModerationQueue /></RequireAuth>} />
              {/* feature/lifecycle-redesign. */}
              <Route path="/para-archivar"          element={<RequireAuth roles={["admin","directora","reparto"]}><ParaArchivar /></RequireAuth>} />
              <Route path="/fallos"          element={<RequireAuth roles={["admin","revisor_fallos"]}><Fallos /></RequireAuth>} />
              <Route path="/fallos/historic" element={<RequireAuth roles={["admin","revisor_fallos"]}><FallosHistoric /></RequireAuth>} />
              {/* feature/fallos-subviews — fallos filtered sub-views by instancia + resultado. */}
              <Route path="/fallos/primera/favorables"     element={<RequireAuth roles={["admin","revisor_fallos"]}><FallosFiltered instancia="primera" resultado="favorable" /></RequireAuth>} />
              <Route path="/fallos/primera/desfavorables"  element={<RequireAuth roles={["admin","revisor_fallos"]}><FallosFiltered instancia="primera" resultado="desfavorable" /></RequireAuth>} />
              <Route path="/fallos/segunda/favorables"     element={<RequireAuth roles={["admin","revisor_fallos"]}><FallosFiltered instancia="segunda" resultado="favorable" /></RequireAuth>} />
              <Route path="/fallos/segunda/desfavorables"  element={<RequireAuth roles={["admin","revisor_fallos"]}><FallosFiltered instancia="segunda" resultado="desfavorable" /></RequireAuth>} />
              <Route path="/fallos/historico"              element={<RequireAuth roles={["admin","revisor_fallos"]}><FallosFiltered mode="historico" /></RequireAuth>} />
              {/* feature/despacho-documents — Documentos del Despacho. */}
              <Route path="/despacho/documentos" element={<RequireAuth><Despacho /></RequireAuth>} />
              <Route path="/admin/auditoria-diaria" element={<RequireAuth roles={["admin","directora"]}><AuditoriaDiaria /></RequireAuth>} />
              <Route path="/actuaciones-urgentes"   element={<RequireAuth roles={["admin","reparto","directora","abogado","insumos"]}><ActuacionesUrgentes /></RequireAuth>} />
              {/* feature/carpetas-tracking — sprint79. Narrowed to the
                  roles that actually work with carpetas. Abogado/insumos/
                  reparto previously had access to the manual registry but
                  the tracking surface is carpetas-team / admin / directora
                  only per the sprint spec. */}
              <Route path="/carpetas"               element={<RequireAuth roles={["admin","directora","carpetas"]}><Carpetas /></RequireAuth>} />
              <Route path="/contestaciones"         element={<RequireAuth roles={["admin","directora","reparto","abogado"]}><Contestaciones /></RequireAuth>} />
              <Route path="/admin/maintenance"      element={<RequireAuth roles={["admin"]}><Maintenance /></RequireAuth>} />
              <Route path="/admin/users/:id/casos"   element={<RequireAuth roles={["admin","directora"]}><UserInsumosCases /></RequireAuth>} />
              <Route path="/abogado"        element={<RequireAuth roles={["admin","abogado"]}><MisCasos /></RequireAuth>} />
              {/* feature/notifications-history. Open to all roles. */}
              <Route path="/notificaciones" element={<RequireAuth><NotificationsHistory /></RequireAuth>} />
              <Route path="/admin/users"      element={<RequireAuth roles={["admin"]}><AdminUsers /></RequireAuth>} />
              <Route path="/admin/audit-log"  element={<RequireAuth roles={["admin"]}><AdminAuditLog /></RequireAuth>} />
              <Route path="/admin/audit"      element={<RequireAuth roles={["admin"]}><AdminAuditLog /></RequireAuth>} />
              <Route path="/admin/sla-breaches" element={<RequireAuth roles={["admin","reparto"]}><AdminSlaBreaches /></RequireAuth>} />
              <Route path="/admin/sla"        element={<RequireAuth roles={["admin","reparto"]}><AdminSlaBreaches /></RequireAuth>} />
              <Route path="/admin/error-queue"element={<RequireAuth roles={["admin"]}><AdminErrorQueue /></RequireAuth>} />
              <Route path="/admin/errors"     element={<RequireAuth roles={["admin"]}><AdminErrorQueue /></RequireAuth>} />
              <Route path="/admin/late-entry-queue" element={<RequireAuth roles={["admin"]}><AdminLateEntry /></RequireAuth>} />
              <Route path="/admin/exports"   element={<RequireAuth roles={["admin","reparto","insumos","directora","carpetas","litisoft"]}><AdminExports /></RequireAuth>} />
              <Route path="/admin/devueltos" element={<RequireAuth roles={["admin","reparto","directora"]}><AdminDevueltos /></RequireAuth>} />
              <Route path="/admin/reports/monthly" element={<RequireAuth roles={["admin","directora"]}><AdminMonthlyReport /></RequireAuth>} />
              {/* feature/bot-litisoft — sprint90. Admin-only LITISOFT bot cockpit. */}
              {/* feature/eva-litisoft — unified admin hub (tabs replace the 5 pages). */}
              <Route path="/admin/eva-litisoft" element={<RequireAuth roles={["admin"]}><EvaLitisoft /></RequireAuth>} />
              {/* Back-compat: old per-page paths redirect to the matching hub tab. */}
              <Route path="/admin/bot-litisoft/reconciliacion" element={<Navigate to="/admin/eva-litisoft?tab=reconciliacion" replace />} />
              <Route path="/admin/bot-litisoft/cola"           element={<Navigate to="/admin/eva-litisoft?tab=cola" replace />} />
              <Route path="/admin/bot-litisoft/monitor"        element={<Navigate to="/admin/eva-litisoft?tab=monitor" replace />} />
              <Route path="/admin/bot-litisoft/historial"      element={<Navigate to="/admin/eva-litisoft?tab=historial" replace />} />
              <Route path="/admin/bot-litisoft/config"         element={<Navigate to="/admin/eva-litisoft?tab=config" replace />} />
              <Route path="/directora/reports"     element={<RequireAuth roles={["admin","directora"]}><AdminMonthlyReport /></RequireAuth>} />

              {/* feature/directora-landing — Directora (Doctora Tania) */}
              <Route path="/directora"            element={<RequireAuth roles={["admin","directora"]}><DirectoraDashboard /></RequireAuth>} />
              <Route path="/directora/devueltos"  element={<RequireAuth roles={["admin","directora"]}><AdminDevueltos /></RequireAuth>} />
              <Route path="/directora/cases"      element={<RequireAuth roles={["admin","directora"]}><CasesList /></RequireAuth>} />
              <Route path="/directora/audit-log"  element={<RequireAuth roles={["admin","directora"]}><AdminAuditLog /></RequireAuth>} />
              <Route path="/directora/exports"    element={<RequireAuth roles={["admin","directora"]}><AdminExports /></RequireAuth>} />
            </Route>
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}
